import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import { Lightning, X, Heart } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { wsClient } from '@/lib/ws';

import { bubbleImageSrc } from '@/lib/bubbleImage';
/**
 * Full-screen "Touch phones" ritual overlay. Used when passing a Bubble.
 * Flow: countdown 3→2→1 → "TOUCH PHONES" with QR fallback → synced reveal of
 * a Nano Banana handwritten "TAKE CARE OF ME" note when the recipient claims.
 * Includes haptic vibration, synchronized across both devices via a
 * `touch_sync` WebSocket broadcast.
 */
const TouchRitual = ({ open, link, onClose, bubble, circle, passToken }) => {
  const [phase, setPhase] = useState('countdown'); // countdown | ready | landed
  const [count, setCount] = useState(3);
  const [note, setNote] = useState(null); // {note_image_b64, note_text, bubble_name}

  // Fetch the cinematic handwritten note once the ritual opens
  useEffect(() => {
    if (!open || !passToken) return;
    api.get(`/passes/${passToken}/touch-note`).then(({ data }) => setNote(data)).catch(() => {});
  }, [open, passToken]);

  // Listen for synced touch event (fired by backend when recipient claims)
  useEffect(() => {
    if (!open) return;
    return wsClient.onEvent((ev) => {
      if (ev.type === 'touch_sync' && bubble && ev.payload?.bubble_id === bubble.id) {
        if (navigator.vibrate) {
          try { navigator.vibrate([0, 30, 60, 30, 200, 50, 300]); } catch {}
        }
        setPhase('landed');
      }
    });
  }, [open, bubble]);

  useEffect(() => {
    if (!open) return;
    setPhase('countdown');
    setCount(3);
    if (navigator.vibrate) try { navigator.vibrate([60, 30, 60, 30, 60]); } catch {}
    const t1 = setTimeout(() => setCount(2), 700);
    const t2 = setTimeout(() => setCount(1), 1400);
    const t3 = setTimeout(() => {
      setPhase((p) => p === 'landed' ? p : 'ready');
      if (navigator.vibrate) try { navigator.vibrate(200); } catch {}
    }, 2100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [open]);

  if (!open) return null;

  const particles = Array.from({ length: 28 });

  return (
    <AnimatePresence>
      <motion.div
        key="ritual"
        data-testid="touch-ritual"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl grid place-items-center overflow-hidden"
      >
        {particles.map((_, i) => (
          <motion.div
            key={i}
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{
              x: Math.cos((i / particles.length) * Math.PI * 2) * (140 + Math.random() * 120),
              y: Math.sin((i / particles.length) * Math.PI * 2) * (140 + Math.random() * 120),
              opacity: [0, 0.9, 0],
            }}
            transition={{ duration: 2.4, repeat: Infinity, delay: (i * 0.05) % 1.6 }}
            className="absolute h-1.5 w-1.5 rounded-full bg-bx-cyan blur-[1px]"
            style={{ left: '50%', top: '50%' }}
          />
        ))}

        <button
          onClick={onClose}
          data-testid="touch-ritual-close"
          className="absolute top-5 right-5 p-2 rounded-full glass-hi text-white/70 hover:text-white"
          aria-label="close ritual"
        >
          <X size={20} weight="bold" />
        </button>

        <div className="relative text-center max-w-xl px-4">
          {phase === 'countdown' && (
            <motion.div
              key={`c-${count}`}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1.2, opacity: 1 }}
              exit={{ scale: 2, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 220 }}
              className="font-display text-[14rem] sm:text-[20rem] leading-none font-black tracking-tighter shimmer-text"
            >
              {count}
            </motion.div>
          )}

          {phase === 'ready' && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan mb-2 flex items-center justify-center gap-2">
                <Lightning size={14} weight="fill" /> energy bridge open
              </div>
              <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter">
                Touch <span className="shimmer-text">phones</span>
              </h1>
              <p className="text-white/65 mt-3 max-w-sm mx-auto text-sm">
                Hold the back of your phones together. Or share the QR below.
              </p>

              <div className="my-8 relative h-56 w-56 mx-auto">
                <span className="absolute inset-0 rounded-full border-2 border-bx-cyan animate-ping" />
                <span className="absolute inset-3 rounded-full border-2 border-bx-magenta animate-ping" style={{ animationDelay: '0.4s' }} />
                <span className="absolute inset-6 rounded-full border-2 border-bx-volt animate-ping" style={{ animationDelay: '0.8s' }} />
                {bubbleImageSrc(bubble) ? (
                  <img
                    src={bubbleImageSrc(bubble)}
                    alt={bubble.name}
                    className="absolute inset-10 rounded-full object-cover breathe ring-2 ring-white/20"
                  />
                ) : (
                  <div className="absolute inset-10 rounded-full bg-gradient-to-br from-bx-cyan via-bx-magenta to-bx-volt breathe" />
                )}
              </div>

              {note?.note_image_b64 && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mx-auto mb-6 max-w-xs"
                  data-testid="touch-ritual-note"
                >
                  <img src={`data:image/png;base64,${note.note_image_b64}`}
                       alt="handwritten note"
                       className="rounded-xl shadow-2xl rotate-[-2deg] ring-1 ring-white/10" />
                  <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-bx-volt">
                    a note from {note.bubble_name || bubble?.name}
                  </div>
                </motion.div>
              )}

              {link && (
                <div className="inline-flex items-center gap-4 glass rounded-2xl px-5 py-4">
                  <div className="bg-white p-2 rounded-xl">
                    <QRCodeCanvas value={link} size={104} fgColor="#050505" bgColor="#ffffff" />
                  </div>
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-widest text-white/60">QR fallback</div>
                    <div className="font-display text-sm font-bold text-white">Scan to claim</div>
                    <button
                      data-testid="touch-ritual-copy"
                      onClick={() => { navigator.clipboard.writeText(link); }}
                      className="mt-2 text-xs text-bx-cyan font-bold"
                    >copy link →</button>
                  </div>
                </div>
              )}

              {circle && circle.circle && (
                <div className="mt-6 max-w-md mx-auto" data-testid="ritual-circle-progress">
                  <div className="text-[10px] uppercase tracking-[0.4em] text-bx-volt mb-3">
                    circle progress · {(circle.chain_holders || []).length} / {circle.chain_limit}
                  </div>
                  <div className="relative h-2 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((circle.chain_holders || []).length / Math.max(1, circle.chain_limit)) * 100}%` }}
                      transition={{ type: 'spring', stiffness: 80 }}
                      className="absolute inset-y-0 left-0 rounded-full"
                      style={{ background: 'linear-gradient(90deg, #00f0ff, #ccff00, #ff007f)' }}
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
                    {(circle.chain_holders || []).map((u, i) => (
                      <span key={i} className="chip text-bx-volt border-bx-volt/40">@{u}</span>
                    ))}
                    {Array.from({ length: Math.max(0, (circle.chain_limit || 0) - (circle.chain_holders || []).length) }).map((_, i) => (
                      <span key={`p-${i}`} className="chip text-white/55">·</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 text-[10px] uppercase tracking-[0.4em] text-white/55">
                NFC bump · QR scan · username send
              </div>
            </motion.div>
          )}

          {phase === 'landed' && (
            <motion.div
              key="landed"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 160 }}
              data-testid="touch-ritual-landed"
            >
              <Heart size={48} weight="fill" className="text-bx-magenta mx-auto" />
              <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter mt-4">
                Energy <span className="shimmer-text">delivered</span>
              </h1>
              <p className="text-white/65 mt-3 max-w-sm mx-auto text-sm">
                The bubble crossed the bridge. Both phones felt it.
              </p>
              <button onClick={onClose} className="btn-volt px-6 py-3 rounded-full text-sm mt-8">
                Done
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TouchRitual;
